import * as React from "react";
import Loader from "../../components/atoms/Loader";
import StudentFilter from "../../components/features/NewStudentFilter";
import { SectionTimelineTab } from "../../components/features/SectionTimelineTab";
import { TimelineResourceFilter } from "../../components/features/TimelineResourceFilter/TimelineResourceFilter";
import { Drawer } from "../../components/general/Drawer";
import { SingleDatePickerWrapper } from "../../components/molecules/SingleDatePickerWrapper";
import { useSingleDateFilterContext } from "../../contexts/SingleDateFilterContext";
import { ResourceFilterEmptyMessages } from "../../domains/TimelineResourceFilter";
import DeviceHelper from "../../helpers/DeviceHelper";
import {
  OutletContextProps,
  WithRouterProps,
} from "../../helpers/RouterHelper";
import { toStringFromDate } from "../../helpers/TimeHelper";
import {
  AuthenticatedPageCallbacks,
  AuthenticatedPageProps,
  AuthenticateRouterProps,
} from "../../hocs/enhanceAuthenticatedPage";
import enhanceSectionsPage from "../../hocs/enhanceSectionsPage";
import { useQueryError } from "../../hooks/http/useQueryError";
import { useOnScrollContentBottom } from "../../hooks/useOnScrollContentBottom";
import { usePushHistory } from "../../hooks/usePushHistory";
import { useQueryString } from "../../hooks/useQueryString";
import { useTimelineResourceFilter } from "../../hooks/useTimelineResourceFilter";
import SectionInterface from "../../interfaces/SectionInterface";
import { TimelineType } from "../../interfaces/TimelineInterface";
import StudentTimelineSection from "./StudentTimelineSection";
import styles from "./styles.scss";
import { useFetchTimelineStudents } from "./useFetchTimelineStudents";

type Props = WithRouterProps<AuthenticateRouterProps> &
  AuthenticatedPageCallbacks &
  Pick<AuthenticatedPageProps, "currentOperator"> &
  OutletContextProps & {
    section: SectionInterface;
  };

// サマリータブ。AI 質問統合表示の凍結（005-freeze-ai-question-timeline）に伴い、
// 学習記録タイムラインのみを表示する。フィルタ UI（TimelineResourceFilter）は
// 骨格を維持したまま画面上は非表示（visible={false}）とする。
const SectionsTimelineSummaryPage = (props: Props) => {
  React.useEffect(() => {
    props.setActiveMenu("timeline");
  }, []);

  const {
    date,
    effectiveDate,
    students,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    onDateChange,
    resourceFilter,
    setResourceFilter,
    refLastElement,
    loadedPageCount,
  } = useSectionsTimelineSummaryPage(props);

  if (!props.section || !props.currentOperator) {
    return null;
  }

  return (
    <>
      <SectionTimelineTab
        sectionId={props.section.id}
        timelineType={TimelineType.TIMELINE_SUMMARY}
        search={props.location.search}
      />
      <StudentFilter sectionId={props.section.id} />
      {/* リソースフィルタは骨格として維持しつつ画面上は非表示。
          凍結解除時（AI 質問再開時）に visible の条件を戻せば復活する */}
      <TimelineResourceFilter
        value={resourceFilter}
        onChange={setResourceFilter}
        visible={false}
      />
      <div className={styles.datePicker}>
        <SingleDatePickerWrapper date={date} onDateChange={onDateChange} />
      </div>

      {isLoading ? (
        <Loader loading={isLoading} />
      ) : (
        <ItemsContainerSwitcher>
          {students && students.length > 0 ? (
            students.map((student) => {
              return (
                <StudentTimelineSection
                  currentOperator={props.currentOperator!}
                  student={student}
                  sectionId={props.section.id}
                  date={effectiveDate}
                  abTestCodes={props.section.abTestCodes}
                  key={`StudentTimelineSection-${student.id}`}
                />
              );
            })
          ) : hasNextPage || isFetchingNextPage ? (
            // 生徒は空だが次のページを取得中
            <Loader loading />
          ) : (
            // 全ページを取得し切っても空のとき
            <p className="mt-7 text-center">
              {ResourceFilterEmptyMessages[resourceFilter]}
            </p>
          )}

          {students && students.length > 0 && isFetchingNextPage && (
            <Loader loading={isFetchingNextPage} />
          )}

          {/* リスト末尾の番兵: 画面内に入ったら次ページを取得する。key の意図は loadedPageCount を参照 */}
          {hasNextPage && (
            <div
              key={loadedPageCount}
              ref={refLastElement}
              data-testid="timeline-summary-load-sentinel"
            />
          )}
        </ItemsContainerSwitcher>
      )}
    </>
  );
};

const useSectionsTimelineSummaryPage = (props: Props) => {
  const queryParams = useQueryString();
  const { pushHistory } = usePushHistory();
  const { date, setStartOfDate, setStartOfToday } =
    useSingleDateFilterContext();
  // URL クエリに date があればその日を、なければ当日を選択日にする（マウント時・date 変化時の両方）
  React.useEffect(() => {
    if (queryParams.date) {
      setStartOfDate(new Date(queryParams.date));
    } else {
      setStartOfToday();
    }
  }, [queryParams.date]);

  // API に渡す表示対象日付（URL クエリの値。未指定はサーバー側で当日扱い）
  const dateParam =
    typeof queryParams.date === "string" ? queryParams.date : undefined;
  // 生徒単位 API（study_records）は date 必須のため、当日も明示して渡す
  const effectiveDate = dateParam ?? toStringFromDate(new Date());

  // フィルタ状態は URL クエリ（resource_filter）を真実の情報源として維持する（骨格維持）。
  // 凍結中は画面上のフィルタ UI が非表示のため値は変化しないが、拡張点として残す
  const { filter: resourceFilter, setFilter: setResourceFilter } =
    useTimelineResourceFilter();

  const {
    students,
    loadedPageCount,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    isFetchNextPageError,
  } = useFetchTimelineStudents({
    sectionId: props.params.sectionId,
    date: dateParam,
    resourceFilter,
    studentFilterParams: queryParams,
    enabled: !!props.section,
  });
  // 初回取得の失敗は共通処理（useQueryError の 401/403 リダイレクト等）に委ねる。
  // 追加読み込みの失敗は取得済み生徒の表示を維持するため抑制し、再試行の停止は下記の
  // onScrollContentBottom で行う
  useQueryError(isFetchNextPageError ? null : error);

  // 次ページの取得は、リスト末尾の番兵が画面内に入ったときに行う（IntersectionObserver による
  // viewport 充填）。スクロールで底に達したときだけでなく、生徒が少数で画面がスクロールできない
  // 場合や、生徒 0 人で表示が空の場合でも、番兵は初期表示で可視になるため、次ページ取得が進む。
  const { refLastElement } = useOnScrollContentBottom({
    // この画面は専用のスクロールコンテナを持たず MainLayout 側でスクロールするため、
    // 監視の基準は viewport（root: null）とする
    rootContentWithScroll: null,
    onScrollContentBottom: () => {
      // 追加読み込みが失敗した後は再試行しない
      if (isLoading || isFetchingNextPage || isFetchNextPageError) {
        return;
      }
      fetchNextPage();
    },
  });

  const onDateChange = (date?: Date) => {
    if (date) {
      pushHistory({ date: toStringFromDate(date) });
    } else {
      pushHistory({});
    }
  };

  return {
    date,
    effectiveDate,
    students,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    onDateChange,
    resourceFilter,
    setResourceFilter,
    refLastElement,
    loadedPageCount,
  };
};

const ItemsContainerSwitcher = ({ children }: React.PropsWithChildren) => {
  if (DeviceHelper.isScreenTablet()) {
    return (
      <>
        <div>{children}</div>
        <Drawer />
      </>
    );
  } else {
    return <div className={styles.itemsContainer}>{children}</div>;
  }
};

const pageInfo = {
  title: "タイムライン",
};

export default enhanceSectionsPage(SectionsTimelineSummaryPage, pageInfo);
